import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button, Table, Select } from "antd";
import Layout from "../../Layout";
import CustomerDetailDrawer from "./components/CustomerDetailDrawer";

interface ComplaintItem {
    key: string;
    code: string;
    customerCode: string;
    customerName: string;
    phone: string;
    category: "Clinic" | "Newhair" | "PTTM" | "Partnership";
    type: string;
    source: string;
    handler: string;
    deadline: string;
    isUrgentDeadline?: boolean;
    status: string;
    statusType: "processing" | "waiting" | "closed";
}

interface DoctorTransferItem {
    key: string;
    code: string;
    customerCode: string;
    customerName: string;
    phone: string;
    category: "Clinic" | "Newhair" | "PTTM" | "Partnership";
    reason: string;
    source: string;
    doctorReceiver: string;
    status: string;
    isWaiting?: boolean;
}

export default function KhieuNaiPage() {
    // State quản lý Drawer chi tiết khách hàng bên phải
    const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);

    // State trạng thái nhân viên CSKH góc dưới
    const [staffStatus, setStaffStatus] = useState<"available" | "break" | "offline">("available");

    // Dữ liệu mẫu Phiếu khiếu nại chuẩn theo hình
    const complaintsData: ComplaintItem[] = [
        {
            key: "1",
            code: "KN-0412",
            customerCode: "KH-15890",
            customerName: "Anh P.Q.D",
            phone: "090***8813",
            category: "Newhair",
            type: "Dịch vụ — chờ lâu",
            source: "Cuộc gọi đến · CS-04 · NV-07",
            handler: "Quản lý CSKH",
            deadline: "Quá 1 ngày",
            isUrgentDeadline: true,
            status: "Đang xử lý",
            statusType: "processing"
        },
        {
            key: "2",
            code: "KN-0411",
            customerCode: "KH-12087",
            customerName: "Chị Đ.T.L",
            phone: "091***4436",
            category: "Clinic",
            type: "Lịch hẹn",
            source: "CSAT 3 (R11)",
            handler: "CSKH",
            deadline: "Còn 20 giờ",
            status: "Chờ khách xác nhận",
            statusType: "waiting"
        },
        {
            key: "3",
            code: "KN-0409",
            customerCode: "KH-10233",
            customerName: "Khách vãng lai",
            phone: "093***1122",
            category: "Clinic",
            type: "Tài chính — ưu đãi chưa áp",
            source: "Cuộc gọi đi · CS-04 · NV-08",
            handler: "Kế toán + Quản lý",
            deadline: "—",
            status: "Đóng",
            statusType: "closed"
        }
    ];

    // Dữ liệu mẫu Phiếu chuyển bác sĩ (R04) chuẩn theo hình
    const doctorTransfersData: DoctorTransferItem[] = [
        {
            key: "1",
            code: "BS-0207",
            customerCode: "KH-16002",
            customerName: "Chị N.H.A",
            phone: "093***5521",
            category: "Clinic",
            reason: "Biến chứng / phản ứng",
            source: "CS-03 · NV-07",
            doctorReceiver: "Bác sĩ phụ trách mảng Clinic",
            status: "Chờ bác sĩ nhận · 12 phút",
            isWaiting: true
        },
        {
            key: "2",
            code: "BS-0205",
            customerCode: "KH-20190",
            customerName: "Khách hàng KH-20190",
            phone: "098***9988",
            category: "Newhair",
            reason: "Rót do bệnh lý",
            source: "KN-07 · Telesales NV-02",
            doctorReceiver: "Bác sĩ phụ trách mảng Clinic",
            status: "Đã xử lý",
            isWaiting: false
        }
    ];

    // Cột bảng Phiếu khiếu nại
    const complaintColumns = [
        {
            title: "Mã phiếu",
            dataIndex: "code",
            key: "code",
            width: 100,
            render: (text: string) => <span className="font-bold text-[#1d4c7d] dark:text-blue-400">{text}</span>
        },
        {
            title: "Khách",
            key: "customer",
            width: 180,
            render: (_: any, record: ComplaintItem) => (
                <span
                    onClick={() => {
                        setSelectedCustomer({
                            code: record.customerCode,
                            name: record.customerName,
                            phone: record.phone,
                            serviceCategory: record.category,
                            csCode: "CS2"
                        });
                    }}
                    className="font-medium text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline block leading-tight"
                >
                    {record.customerCode} · {record.customerName}
                </span>
            )
        },
        {
            title: "Loại",
            dataIndex: "type",
            key: "type",
            width: 180,
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200">{text}</span>
        },
        {
            title: "Tạo từ",
            dataIndex: "source",
            key: "source",
            width: 200,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300">{text}</span>
        },
        {
            title: "Người xử lý",
            dataIndex: "handler",
            key: "handler",
            width: 150,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300 font-medium">{text}</span>
        },
        {
            title: (
                <div className="flex items-center gap-1">
                    <span>Hạn xử lý</span>
                    <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded font-bold cursor-pointer">[?]</span>
                </div>
            ),
            key: "deadline",
            dataIndex: "deadline",
            width: 130,
            render: (_: any, record: ComplaintItem) => (
                <span className={record.isUrgentDeadline ? "text-red-600 font-bold dark:text-red-400" : "text-gray-700 dark:text-gray-300"}>
                    {record.deadline}
                </span>
            )
        },
        {
            title: "Trạng thái",
            key: "status",
            dataIndex: "status",
            width: 160,
            render: (_: any, record: ComplaintItem) => (
                <span className={record.statusType === "processing" ? "text-red-600 font-bold dark:text-red-400" : "text-gray-800 dark:text-gray-200 font-medium"}>
                    {record.status}
                </span>
            )
        }
    ];

    // Cột bảng Phiếu chuyển bác sĩ (R04) - ĐÃ FIX LỖI OBJECT AS REACT CHILD
    const doctorTransferColumns = [
        {
            title: "Mã phiếu",
            dataIndex: "code",
            key: "code",
            width: 100,
            render: (text: string) => <span className="font-bold text-[#1d4c7d] dark:text-blue-400">{text}</span>
        },
        {
            title: "Khách",
            key: "customer",
            width: 180,
            render: (_: any, record: DoctorTransferItem) => (
                <span
                    onClick={() => {
                        setSelectedCustomer({
                            code: record.customerCode,
                            name: record.customerName,
                            phone: record.phone,
                            serviceCategory: record.category,
                            csCode: "CS2"
                        });
                    }}
                    className="font-medium text-[#1d4c7d] dark:text-blue-400 cursor-pointer hover:underline block leading-tight"
                >
                    {record.customerCode} · {record.customerName}
                </span>
            )
        },
        {
            title: "Lý do",
            dataIndex: "reason",
            key: "reason",
            width: 180,
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200 font-medium">{text}</span>
        },
        {
            title: "Tạo từ",
            dataIndex: "source",
            key: "source",
            width: 160,
            render: (text: string) => <span className="text-gray-700 dark:text-gray-300">{text}</span>
        },
        {
            title: (
                <div className="flex items-center gap-1">
                    <span>Bác sĩ nhận</span>
                    <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded font-bold cursor-pointer">[?]</span>
                    <span>tuyến nhận</span>
                </div>
            ),
            key: "doctorReceiver",
            dataIndex: "doctorReceiver",
            width: 250,
            render: (text: string) => <span className="text-gray-800 dark:text-gray-200 font-medium">{text}</span>
        },
        {
            title: "Trạng thái",
            key: "status",
            dataIndex: "status",
            width: 200,
            render: (_: any, record: DoctorTransferItem) => (
                <span className={record.isWaiting ? "text-red-600 font-bold dark:text-red-400" : "text-gray-700 dark:text-gray-300 font-medium"}>
                    {record.status}
                </span>
            )
        }
    ];

    const getStatusDotColor = () => {
        switch (staffStatus) {
            case "available": return "#52c41a";
            case "break": return "#d48806";
            case "offline": return "#8c8c8c";
            default: return "#52c41a";
        }
    };

    return (
        <Layout>
            <Helmet>
                <title>Khiếu nại — phiếu khiếu nại và phiếu chuyển bác sĩ | Khothi</title>
            </Helmet>

            <div className="min-h-screen text-xs flex flex-col justify-between transition-colors duration-200">
                <div className="flex flex-1 overflow-hidden gap-4">
                    
                    {/* KHUNG NỘI DUNG CHÍNH BÊN TRÁI */}
                    <div className="space-y-4 overflow-y-auto pb-20 w-full">
                        
                        {/* 1. Header Tiêu đề & Quy tắc */}
                        <div className="flex flex-wrap items-center justify-between border-b border-gray-200 dark:border-gray-800 pb-2 gap-2">
                            <div className="flex flex-wrap items-center gap-2 sm:gap-3 border-l-4 border-[#1d4c7d] pl-2">
                                <span className="font-bold text-[#1d4c7d] dark:text-blue-400 text-sm">
                                    Khiếu nại — phiếu khiếu nại và phiếu chuyển bác sĩ
                                </span>
                                <span className="text-gray-600 dark:text-gray-400 flex items-center gap-1">
                                    Vai trò: CSKH, Trưởng phòng CSKH 
                                    <span className="bg-[#d97706] text-white text-[10px] px-1 py-0.2 rounded font-bold cursor-pointer">[?]</span>, bác sĩ
                                </span>
                                <span className="text-gray-500 dark:text-gray-400 hidden xl:inline">
                                    Quy tắc: Tạo từ mã CS-04 / CS-03 / KN-07 hoặc CS ≤ 3 (R11) · R04 · SOP CSKH mục 3
                                </span>
                            </div>
                        </div>

                        {/* 2. Dòng Khung Cam DỮ LIỆU MINH HOẠ */}
                        <div className="border border-dashed border-orange-400 bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 text-center py-1 font-bold tracking-wider rounded text-xs">
                            DỮ LIỆU MINH HOẠ — tên khách, mã nhân viên và mọi con số là giả
                        </div>

                        {/* 3. PHẦN 1: PHIẾU KHIẾU NẠI */}
                        <div className="space-y-2">
                            <div className="font-bold text-[#1d4c7d] dark:text-blue-400 text-xs">
                                Phiếu khiếu nại
                            </div>
                            <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                                <Table
                                    columns={complaintColumns}
                                    dataSource={complaintsData}
                                    pagination={false}
                                    size="small"
                                    className="custom-complaint-table"
                                    scroll={{ x: "max-content" }}
                                    rowClassName={(record, index) =>
                                        index % 2 === 1
                                            ? "bg-slate-50/80 dark:bg-[#252525]"
                                            : "bg-white dark:bg-[#1f1f1f]"
                                    }
                                />
                            </div>
                            <div className="text-gray-500 text-[11px] pt-0.5 leading-relaxed">
                                Thẩm quyền theo SOP CSKH: CSKH xin lỗi, tăng voucher ≤ 500k, đổi lịch, kết nối bác sĩ. Hoàn tiền / bồi thường &gt; 500k, khiếu nại lan mạng xã hội, vấn đề y tế nghiêm trọng → leo thang Quản lý / CCO.
                            </div>
                        </div>

                        {/* 4. PHẦN 2: PHIẾU CHUYỂN BÁC SĨ (R04) */}
                        <div className="space-y-2 pt-2">
                            <div className="font-bold text-[#1d4c7d] dark:text-blue-400 text-xs">
                                Phiếu chuyển bác sĩ (R04)
                            </div>
                            <div className="bg-white dark:bg-[#1f1f1f] rounded border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                                <Table
                                    columns={doctorTransferColumns}
                                    dataSource={doctorTransfersData}
                                    pagination={false}
                                    size="small"
                                    className="custom-complaint-table"
                                    scroll={{ x: "max-content" }}
                                    rowClassName={(record, index) =>
                                        index % 2 === 1
                                            ? "bg-slate-50/80 dark:bg-[#252525]"
                                            : "bg-white dark:bg-[#1f1f1f]"
                                    }
                                />
                            </div>
                        </div>

                    </div>

                    {/* 5. DRAWER CHI TIẾT KHÁCH HÀNG BÊN PHẢI */}
                    {selectedCustomer && (
                        <CustomerDetailDrawer
                            customer={selectedCustomer}
                            onClose={() => setSelectedCustomer(null)}
                        />
                    )}
                </div>

                {/* Floating Widget Thanh Trạng Thái (PC 1 hàng, Mobile tự động xuống 2 hàng) */}
                <div className="sticky bottom-3 z-27 sm:mx-0 bg-[#1d4c7d] dark:bg-[#142f4c] text-white rounded-lg shadow-xl px-3.5 py-2.5 flex flex-col sm:flex-row items-center justify-between sm:justify-start gap-2 sm:gap-3 w-full sm:w-fit border border-[#3E91AB]">
                    
                    {/* Phần thông tin nhân viên và Select trạng thái */}
                    <div className="flex items-center justify-between sm:justify-start w-full sm:w-auto gap-2">
                        <div className="flex items-center gap-2">
                            <span
                                className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                                style={{ backgroundColor: getStatusDotColor() }}
                            ></span>
                            <span className="font-bold text-sm whitespace-nowrap">
                                NV-07 · CSKH
                            </span>
                        </div>

                        {/* Trên mobile hiển thị Select ở đây cho cân đối dòng 1 */}
                        <div className="block sm:hidden">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[110px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>
                    </div>

                    {/* Phần Select (cho PC) và các nút bấm hành động */}
                    <div className="flex items-center justify-between sm:justify-start gap-2 w-full sm:w-auto border-t border-blue-400/30 pt-2 sm:border-t-0 sm:pt-0">
                        
                        {/* Trên PC hiển thị Select ở vị trí này */}
                        <div className="hidden sm:block">
                            <Select
                                size="small"
                                value={staffStatus}
                                onChange={(val) => setStaffStatus(val)}
                                className="w-[125px] text-sm flex-shrink-0"
                                options={[
                                    { label: "Sẵn sàng", value: "available" },
                                    { label: "Tạm nghỉ", value: "break" },
                                    { label: "Ngoại tuyến", value: "offline" },
                                ]}
                            />
                        </div>

                        <span className="text-xs text-gray-200 hidden md:inline whitespace-nowrap">
                            Không có cuộc gọi
                        </span>

                        <div className="flex items-center gap-1.5 w-full sm:w-auto justify-end">
                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Mô phỏng gọi đến
                            </Button>

                            <Button
                                size="small"
                                className="bg-white text-gray-800 hover:bg-gray-100 font-medium text-xs border-0 h-7 px-2.5 flex-1 sm:flex-initial"
                            >
                                Gọi số mới
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
}